import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useEffect, useState } from 'react';
import eventService from '../../services/eventService';
import { mediaUrl } from '../../utils/helpers';
import { toast } from 'react-toastify';

const Home = () => {
  const { user } = useAuth();

  const fallbackImages = [
    'https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=1200',
    'https://images.unsplash.com/photo-1546519638-68e109498ffc?w=1200',
    'https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=1200',
  ];

  const [events, setEvents] = useState([]);

  const slides = React.useMemo(() => {
    if (!events || events.length === 0) {
      return fallbackImages.map(img => ({ type: 'image', src: img }));
    }
    return events.map(event => {
      if (event.video_link) {
        return { type: 'video', src: mediaUrl(event.video_link) };
      }
      if (event.images?.[0]?.image_path) {
        return { type: 'image', src: mediaUrl(event.images[0].image_path) };
      }
      return { type: 'image', src: fallbackImages[0] };
    });
  }, [events]);

  const fetchEvents = async (page = 1) => {
    try {
      const response = await eventService.getEvents({ page, per_page: 5 });
      const paginated = response.data.data;
      setEvents(paginated.data);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events');
      setEvents([]);
    }
  };

  useEffect(() => { fetchEvents(1); }, []);

  const [index, setIndex] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setIndex(prev => slides.length ? (prev + 1) % slides.length : 0), 5000);
    return () => clearInterval(interval);
  }, [index, slides]);

  const prevSlide = () => setIndex(prev => slides.length ? (prev - 1 + slides.length) % slides.length : 0);
  const nextSlide = () => setIndex(prev => slides.length ? (prev + 1) % slides.length : 0);

  const industries = [
    'Venues, Theaters & Arenas', 'Concerts & Concert Tours', 'Performing Arts',
    'Sports', 'Schools', 'Festivals', 'Ballrooms', 'Recurring Events',
    'Amusement Parks', 'Nonprofit & Charity', 'Galas & Fundraising', 'Tours & Transportation',
  ];

  const features = [
    {
      icon: (
        <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      title: 'Lightning Fast',
      desc: 'Book your tickets in seconds with our streamlined checkout process.',
    },
    {
      icon: (
        <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      title: 'Digital Tickets',
      desc: 'Get instant QR code tickets on your phone. Eco-friendly and always accessible.',
    },
    {
      icon: (
        <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      title: 'Secure Payment',
      desc: 'Your transactions are protected with bank-level security and encryption.',
    },
  ];

  return (
    <div className="min-h-screen">

      {/* ── Hero Carousel ── */}
      <section className="relative overflow-hidden" style={{ borderRadius: '0 0 2rem 2rem' }}>
        <div className="relative h-screen">
          {/* Slides */}
          {slides.map((slide, i) => (
            <div
              key={i}
              className={`absolute inset-0 transition-opacity duration-1000 ${i === index ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
            >
              {slide.type === 'video' ? (
                <video src={slide.src} autoPlay muted loop playsInline className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-cover bg-center" style={{ backgroundImage: `url(${slide.src})` }} />
              )}
              <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/70" />
            </div>
          ))}

          {/* Arrows */}
          <button type="button" onClick={prevSlide} aria-label="Previous slide" className="hero-nav absolute left-5 top-1/2 -translate-y-1/2 z-20">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button type="button" onClick={nextSlide} aria-label="Next slide" className="hero-nav absolute right-5 top-1/2 -translate-y-1/2 z-20">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Slide dots */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
            {slides.map((_, i) => (
              <button key={i} onClick={() => setIndex(i)}
                className={`h-2 rounded-full transition-all duration-300 ${i === index ? 'w-6 bg-white' : 'w-2 bg-white/50'}`} />
            ))}
          </div>

          {/* Hero text */}
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center text-center px-4">
            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-5 leading-tight drop-shadow-lg">
              Discover Amazing<br />
              <span className="text-purple-300">Events Near You</span>
            </h1>
            <p className="text-gray-200 text-lg max-w-xl mb-8">
              Buy tickets instantly, get digital passes, and never miss out on the experiences you love.
            </p>
            <Link to="/events" className="nav-link" style={{ display: 'inline-flex', flexDirection: 'row', alignItems: 'center', flexWrap: 'nowrap', gap: '12px', padding: '16px 40px', borderRadius: '16px', fontWeight: '700', color: '#fff', fontSize: '20px', whiteSpace: 'nowrap', background: 'linear-gradient(135deg,#8B5CF6,#7C3AED)', boxShadow: '0 12px 32px rgba(139,92,246,0.55)', transition: 'transform 0.2s' }}>
              <svg style={{ width: '22px', height: '22px', flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <span>Explore Events</span>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-gray-900 mb-3">
              Why Choose <span style={{ color: '#7C3AED' }}>Ticketaty</span>
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto">Seamless from browsing to booking. Join thousands who trust Ticketaty for every event experience.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <div key={i} className="rounded-2xl p-7 text-center transition-all hover:-translate-y-1 hover:shadow-xl" style={{ background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(12px)', border: '1px solid rgba(196,181,253,0.3)', boxShadow: '0 4px 20px rgba(139,92,246,0.08)' }}>
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-5 shadow-lg" style={{ background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)' }}>
                  {f.icon}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{f.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section className="py-20 px-4" style={{ background: 'linear-gradient(135deg, #1E1B4B 0%, #2D1B69 100%)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-extrabold text-white mb-3">How It Works</h2>
            <p className="text-purple-300">Get your tickets in just 3 simple steps</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {[
              { n: '1', title: 'Find Events', desc: 'Browse thousands of events across various categories and locations.' },
              { n: '2', title: 'Select Tickets', desc: 'Choose your preferred ticket type and quantity, then proceed to checkout.' },
              { n: '3', title: 'Enjoy Event', desc: 'Show your QR code at the entrance and enjoy an unforgettable experience.' },
            ].map((step, i) => (
              <div key={i} className="text-center relative">
                {i < 2 && (
                  <div className="hidden md:block absolute top-9 left-[calc(50%+40px)] right-[-calc(50%-40px)] h-0.5 border-t-2 border-dashed border-purple-600 w-full" />
                )}
                <div className="w-18 h-18 rounded-full flex items-center justify-center mx-auto mb-5 text-2xl font-extrabold text-white shadow-2xl" style={{ width: '72px', height: '72px', background: 'linear-gradient(135deg, #8B5CF6, #6D28D9)', boxShadow: '0 0 30px rgba(139,92,246,0.4)' }}>
                  {step.n}
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                <p className="text-purple-300 text-sm leading-relaxed max-w-xs mx-auto">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Industries ── */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl font-extrabold text-gray-900 mb-3">
            Industries That Trust <span style={{ color: '#7C3AED' }}>Ticketaty</span>
          </h2>
          <p className="text-gray-500 mb-10 max-w-2xl mx-auto text-sm leading-relaxed">
            From large-scale venues and festivals to schools, nonprofits, and tours — Ticketaty delivers a secure, flexible, real-time ticketing platform for events of every size.
          </p>

          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {industries.map((item, i) => (
              <span
                key={i}
                className="px-4 py-2 rounded-full text-sm font-medium cursor-default transition-all hover:scale-105"
                style={{ background: 'rgba(255,255,255,0.8)', border: '1px solid rgba(196,181,253,0.5)', color: '#4C1D95', boxShadow: '0 2px 8px rgba(139,92,246,0.1)' }}
              >
                {item}
              </span>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" className="nav-link inline-flex items-center justify-center gap-2 px-7 py-3 rounded-xl font-semibold text-white text-sm transition-all hover:scale-105" style={{ background: 'linear-gradient(135deg,#8B5CF6,#7C3AED)', boxShadow: '0 8px 24px rgba(139,92,246,0.35)' }}>
              Get Started Free
            </Link>
            <Link to="/events" className="nav-link inline-flex items-center justify-center gap-2 px-7 py-3 rounded-xl font-semibold text-sm transition-all hover:scale-105" style={{ background: 'rgba(255,255,255,0.8)', border: '2px solid #8B5CF6', color: '#7C3AED' }}>
              Browse Events
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;
